/**
 * <gff-mixed-slider> — Manual 1-up full-bleed snap story for mixed boxes.
 *
 * Markup contract (Liquid emits this):
 *   <gff-mixed-slider>
 *     <div data-gff-mixed-track>  <!-- overflow-x + scroll-snap -->
 *       <article data-gff-mixed-slide>…</article>
 *     </div>
 *     <button data-dir="prev">…</button>
 *     <button data-dir="next">…</button>
 *     <div data-gff-mixed-dots>
 *       <button data-gff-mixed-dot data-index="0">…</button>
 *     </div>
 *     <div data-gff-mixed-progress>
 *       <div data-gff-mixed-progress-thumb></div>
 *     </div>
 *   </gff-mixed-slider>
 *
 * No autoplay. Arrows wrap. Dots and progress stay in sync with snap.
 * prefers-reduced-motion → instant scroll, no smooth behavior.
 * No-JS fallback: native overflow-x + CSS scroll-snap.
 */
class GffMixedSlider extends HTMLElement {
  #track = null;
  #slides = [];
  #prevBtn = null;
  #nextBtn = null;
  #dots = [];
  #progressThumb = null;
  #index = 0;
  #rafId = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-gff-mixed-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-gff-mixed-slide]'));
    this.#prevBtn = this.querySelector('[data-dir="prev"]');
    this.#nextBtn = this.querySelector('[data-dir="next"]');
    this.#dots = Array.from(this.querySelectorAll('[data-gff-mixed-dot]'));
    this.#progressThumb = this.querySelector('[data-gff-mixed-progress-thumb]');

    if (!this.#track || this.#slides.length === 0) return;

    this.#prevBtn?.addEventListener('click', () => this.#go(this.#index - 1));
    this.#nextBtn?.addEventListener('click', () => this.#go(this.#index + 1));

    this.#dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = Number(dot.dataset.index);
        if (Number.isFinite(i)) this.#go(i);
      });
    });

    this.#track.addEventListener('scroll', () => this.#queueSync(), { passive: true });
    this.#track.addEventListener('keydown', (event) => this.#onKey(event));

    window.addEventListener('resize', () => this.#queueSync(), { passive: true });
    this.#queueSync();
  }

  disconnectedCallback() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #onKey(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#go(this.#index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#go(this.#index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.#go(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.#go(this.#slides.length - 1);
    }
  }

  #wrap(i) {
    const n = this.#slides.length;
    return ((i % n) + n) % n;
  }

  #go(i) {
    const next = this.#wrap(i);
    const slide = this.#slides[next];
    if (!slide) return;
    const behavior = this.#reducedMotion() ? 'auto' : 'smooth';
    this.#track.scrollTo({ left: slide.offsetLeft, behavior });
    this.#index = next;
    this.#paint();
  }

  #queueSync() {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#syncFromScroll();
    });
  }

  #syncFromScroll() {
    const left = this.#track.scrollLeft;
    const width = this.#track.clientWidth || 1;
    const i = Math.round(left / width);
    this.#index = Math.min(Math.max(i, 0), this.#slides.length - 1);
    this.#paint();
  }

  #supportsInert() {
    return 'inert' in HTMLElement.prototype;
  }

  #focusables(slide) {
    return slide.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
  }

  #setSlideOpen(slide, on) {
    if (this.#supportsInert()) {
      slide.inert = !on;
    } else {
      this.#focusables(slide).forEach((el) => {
        if (on) {
          if (!el.hasAttribute('data-gff-tabindex')) return;
          const prev = el.getAttribute('data-gff-tabindex');
          el.removeAttribute('data-gff-tabindex');
          if (prev === '') el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', prev);
        } else {
          if (!el.hasAttribute('data-gff-tabindex')) {
            el.setAttribute('data-gff-tabindex', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '');
          }
          el.setAttribute('tabindex', '-1');
        }
      });
    }

    if (!on && slide.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  #paint() {
    this.#slides.forEach((slide, i) => {
      const on = i === this.#index;
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      slide.classList.toggle('is-active', on);
      this.#setSlideOpen(slide, on);
    });

    this.#dots.forEach((dot, i) => {
      const on = i === this.#index;
      if (on) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
      dot.classList.toggle('is-active', on);
    });

    if (this.#progressThumb) {
      const n = this.#slides.length;
      const widthPct = 100 / n;
      this.#progressThumb.style.width = `${widthPct}%`;
      this.#progressThumb.style.left = `${widthPct * this.#index}%`;
    }
  }
}

if (!customElements.get('gff-mixed-slider')) {
  customElements.define('gff-mixed-slider', GffMixedSlider);
}
